import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone, Home, Trees, Scissors, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SEOHead, localBusinessSchema } from '../components/SEOHead';
import { AGENCIES } from '../lib/agencies-data';
import { getZones, getMainCities, cityPath, CITIES, fetchDynamicCities, getDynamicCities, SEARCH_RADIUS_KM } from '../lib/cities-data';
import { CitySearch } from '../components/CitySearch';
import { serviceCityPath } from '../lib/service-city-data';
import { COMPANY_INFO } from '../lib/utils';
import { VIEWPORT_ONCE } from '../lib/motion';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.06 } } };

export default function CitiesIndexPage() {
  const agency = AGENCIES['artois.ptitservices'];
  const zones = getZones();
  const main = getMainCities();
  const [dynamicCities, setDynamicCities] = useState(getDynamicCities());

  useEffect(() => {
    let cancelled = false;
    fetchDynamicCities().then(() => { if (!cancelled) setDynamicCities(getDynamicCities()); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="cities-index-page">
      <SEOHead
        title="Villes desservies dans l'Artois"
        description={`Ptitservices intervient dans ${CITIES.length} communes autour de Servins (40 km) : Arras, Lens, Liévin, Béthune, Bruay, Hénin-Beaumont, Douai… Ménage dès 14,99 €/h et jardinage dès 24,95 €/h après crédit d'impôt.`}
        path="/services-a-domicile"
        schema={localBusinessSchema(agency)}
      />

      <section className="bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)] py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Villes desservies</span>
          </nav>
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp}>
              <Badge className="mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"><MapPin className="h-4 w-4 mr-2" /> {CITIES.length} communes desservies</Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl font-semibold tracking-tight mb-3">Services à domicile dans l'Artois : <span className="text-[hsl(var(--primary))]">votre ville</span></motion.h1>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl">Notre agence de Servins intervient dans un rayon de 40 km : agglomérations de Lens-Liévin, Béthune-Bruay, Arrageois, Hénin-Carvin, Ternois et limite du Douaisis. Choisissez votre commune pour découvrir nos services, nos tarifs après crédit d'impôt et demander un devis.</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Village / town search — every commune within 30 km is served */}
      <section className="py-10 sm:py-14 -mt-2" data-testid="city-search-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <Card className="p-6 sm:p-8 border border-[hsl(var(--primary))]/20 bg-card shadow-sm">
            <div className="text-center mb-5">
              <Badge className="mb-3 bg-[hsl(var(--brand-mint))] text-foreground hover:bg-[hsl(var(--brand-mint))]"><Search className="h-3.5 w-3.5 mr-1.5 text-[hsl(var(--primary))]" /> Votre commune n'est pas dans la liste ?</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-2">Recherchez votre ville ou votre village</h2>
              <p className="text-muted-foreground text-sm max-w-2xl mx-auto">Les villages sont desservis exactement comme les villes : toute commune à moins de {SEARCH_RADIUS_KM} km de Servins peut être ajoutée en un clic et sa page locale est créée immédiatement.</p>
            </div>
            <CitySearch onAdded={() => setDynamicCities(getDynamicCities())} />
          </Card>
        </div>
      </section>

      {/* Main cities */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-6">Villes principales</h2>
          <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="main-cities-grid">
            {main.map((c) => (
              <motion.div key={c.slug} variants={fadeUp}>
                <Link to={cityPath(c)}>
                  <Card className="p-4 h-full border border-border hover:border-[hsl(var(--primary))] hover:shadow-md transition-[box-shadow,border-color] group">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold group-hover:text-[hsl(var(--primary))] transition-colors">{c.name}</h3>
                        <p className="text-xs text-muted-foreground">{c.cp}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-[hsl(var(--primary))]" />
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Communes added through the search */}
      {dynamicCities.length > 0 && (
        <section className="py-10 sm:py-12 bg-[hsl(var(--secondary))]/60" data-testid="dynamic-cities-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-2">Villages et communes récemment ajoutés</h2>
            <p className="text-sm text-muted-foreground mb-5">{dynamicCities.length} commune(s) ajoutée(s) par nos visiteurs — toutes à moins de {SEARCH_RADIUS_KM} km de notre agence.</p>
            <div className="flex flex-wrap gap-2" data-testid="dynamic-cities-list">
              {dynamicCities.map((c) => (
                <Link key={c.slug} to={cityPath(c)} data-testid="dynamic-city-link">
                  <Badge variant="outline" className="px-3 py-1.5 text-sm bg-card hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]">{c.name}{c.cp ? ` (${c.cp})` : ''}{c.distanceKm ? ` · ${String(c.distanceKm).replace('.', ',')} km` : ''}</Badge>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ménage / Jardinage par ville */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[
            { key: 'menage', icon: Home, title: 'Femme de ménage par ville', desc: 'Entretien du domicile, repassage, vitres — dès 14,99 €/h après crédit d\'impôt.', prefix: 'Ménage à' },
            { key: 'jardinage', icon: Trees, title: 'Jardinier par ville', desc: 'Tonte, taille de haies, désherbage — dès 24,95 €/h après crédit d\'impôt.', prefix: 'Jardinage à' },
            { key: 'taille-haies', icon: Scissors, title: 'Taille de haies par ville', desc: 'Thuyas, lauriers, charmilles — taille d\'entretien, de forme ou réduction, déchets ramassés.', prefix: 'Haies à' },
          ].map((block) => (
            <Card key={block.key} className="p-6" data-testid={`index-${block.key}-links`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><block.icon className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
                <h2 className="font-['Space_Grotesk'] text-xl font-semibold">{block.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">{block.desc}</p>
              <div className="flex flex-wrap gap-2">
                {main.map((c) => (
                  <Link key={c.slug} to={serviceCityPath(block.key, c)} className="px-3 py-1.5 rounded-full border border-border bg-card text-sm hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors">{block.prefix} {c.name}</Link>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* All zones */}
      <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight mb-6">Toutes les communes par secteur</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="zones-grid">
            {zones.map((zone) => (
              <Card key={zone.region} className="p-5">
                <h3 className="font-['Space_Grotesk'] font-semibold text-sm mb-3 text-[hsl(var(--primary))]">{zone.region}</h3>
                <ul className="flex flex-wrap gap-1.5">
                  {zone.cities.map((c) => (
                    <li key={c.slug}>
                      <Link to={cityPath(c)} className="inline-block text-xs px-2.5 py-1 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))] hover:text-white transition-colors">{c.name}</Link>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
          <div className="mt-8 p-5 rounded-xl bg-[hsl(var(--brand-mint))]/40 text-center">
            <p className="text-sm text-muted-foreground mb-3">Votre commune n'apparaît pas ? Appelez-nous, nous vérifions ensemble.</p>
            <a href={`tel:${COMPANY_INFO.phoneRaw}`}><Button variant="outline" size="sm"><Phone className="mr-2 h-4 w-4" />{COMPANY_INFO.phone}</Button></a>
          </div>
        </div>
      </section>
    </div>
  );
}
